// ─── src/components/filters/FilterBar.jsx ─────────────────────────────────────
// Shared, config-driven filter bar for every category/country/region page.
// Desktop: inline dropdowns. Mobile: compact pill + bottom sheet.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useRef, useEffect } from "react";

const NU       = "var(--font-body)";
const GOLD     = '#d39a21';
const GOLD_DIM = '#b8841c';
const STONE    = '#F6F3EE';

// ── "All" label defaults per filter key ─────────────────────────────────────
const ALL_LABELS = {
  country:    'All Countries',
  region:     'All Regions',
  venueType:  'All Venue Types',
  priceRange: 'Any Price',
  guests:     'Any Size',
  specialty:  'All Specialties',
  tier:       'All Tiers',
  budget:     'Any Budget',
};
const allLabel = (key) => ALL_LABELS[key] || 'All';

// ── Desktop: inline Select dropdown ─────────────────────────────────────────
function Select({ label, value, defaultVal, onChange, options, dark }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const active = value !== defaultVal;

  const inkMuted = dark ? 'rgba(240,236,228,0.38)' : 'rgba(42,32,18,0.42)';
  const bdr      = dark ? 'rgba(255,255,255,0.14)' : 'rgba(150,128,90,0.38)';
  const bg       = dark ? 'rgba(255,255,255,0.03)' : 'transparent';
  const bgActive = dark ? 'rgba(211,154,33,0.09)' : 'rgba(211,154,33,0.06)';

  const selectedLabel = options.find(o => o.value === value)?.label || options[0]?.label;

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div ref={wrapRef} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{
        fontFamily: NU, fontSize: 9, letterSpacing: '0.18em',
        textTransform: 'uppercase', fontWeight: 700,
        color: active ? GOLD_DIM : inkMuted,
        transition: 'color 0.2s',
      }}>{label}</span>

      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            minWidth: 148, width: '100%',
            padding: '10px 32px 10px 14px',
            fontFamily: NU, fontSize: 13, fontWeight: active ? 500 : 400,
            textAlign: 'left', letterSpacing: '0.01em',
            color: active ? (dark ? '#f0ece4' : 'rgba(42,32,18,0.92)') : inkMuted,
            background: active ? bgActive : bg,
            border: `1px solid ${active ? GOLD : bdr}`,
            borderRadius: 0, cursor: 'pointer', outline: 'none',
            transition: 'border-color 0.18s, background 0.18s, color 0.18s',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
          onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = dark ? 'rgba(255,255,255,0.28)' : 'rgba(150,128,90,0.65)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = active ? GOLD : bdr; }}
        >
          {selectedLabel}
        </button>

        <span style={{
          position: 'absolute', right: 11, top: '50%',
          transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
          fontSize: 9, pointerEvents: 'none',
          color: active ? GOLD : inkMuted,
          transition: 'transform 0.18s ease',
        }}>▾</span>

        {open && (() => {
          const cols = options.length > 20 ? 3 : options.length > 8 ? 2 : 1;
          const panelMinW = cols === 3 ? 540 : cols === 2 ? 360 : '100%';
          return (
          <div style={{
            position: 'absolute', top: 'calc(100% + 3px)', left: 0,
            zIndex: 500, minWidth: panelMinW,
            background: '#16120d',
            border: `1px solid rgba(211,154,33,0.25)`,
            borderTop: `2px solid ${GOLD}`,
            borderRadius: 0,
            boxShadow: '0 16px 48px rgba(0,0,0,0.50)',
            overflow: 'hidden',
            animation: 'lwd-select-in 0.14s ease',
          }}>
            {options.length > 8 ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cols},1fr)`,
                gap: 0,
                maxHeight: 380,
                overflowY: 'auto',
              }}>
                {options.map((o) => {
                  const isSel = o.value === value;
                  return (
                    <button
                      key={o.value}
                      onClick={() => { onChange(o.value); setOpen(false); }}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '9px 14px',
                        fontFamily: NU, fontSize: 12,
                        fontWeight: isSel ? 600 : 400,
                        color: isSel ? GOLD : 'rgba(240,232,218,0.72)',
                        background: isSel ? 'rgba(211,154,33,0.12)' : 'transparent',
                        border: 'none',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        borderRight: '1px solid rgba(255,255,255,0.04)',
                        cursor: 'pointer',
                        transition: 'background 0.12s, color 0.12s',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        letterSpacing: '0.01em',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(211,154,33,0.14)';
                        e.currentTarget.style.color = '#f5efe3';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = isSel ? 'rgba(211,154,33,0.12)' : 'transparent';
                        e.currentTarget.style.color = isSel ? GOLD : 'rgba(240,232,218,0.72)';
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              options.map((o, i) => {
                const isSel = o.value === value;
                return (
                  <button
                    key={o.value}
                    onClick={() => { onChange(o.value); setOpen(false); }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '10px 16px',
                      fontFamily: NU, fontSize: 13,
                      fontWeight: isSel ? 600 : 400,
                      color: isSel ? GOLD : 'rgba(240,232,218,0.72)',
                      background: isSel ? 'rgba(211,154,33,0.12)' : 'transparent',
                      border: 'none',
                      borderBottom: i < options.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                      cursor: 'pointer',
                      transition: 'background 0.12s, color 0.12s',
                      whiteSpace: 'nowrap', letterSpacing: '0.01em',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(211,154,33,0.14)';
                      e.currentTarget.style.color = '#f5efe3';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = isSel ? 'rgba(211,154,33,0.12)' : 'transparent';
                      e.currentTarget.style.color = isSel ? GOLD : 'rgba(240,232,218,0.72)';
                    }}
                  >
                    {o.label}
                  </button>
                );
              })
            )}
          </div>
          );
        })()}
      </div>
    </div>
  );
}

// ── Mobile: single filter section inside bottom sheet ────────────────────────
function SheetSection({ label, value, defaultVal, options, onChange, dark }) {
  const isActive = value && value !== defaultVal;
  const inkMuted = dark ? 'rgba(240,236,228,0.38)' : 'rgba(42,32,18,0.42)';

  return (
    <div style={{ marginBottom: 22 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{
          fontFamily: NU, fontSize: 9, letterSpacing: '0.2em',
          textTransform: 'uppercase', fontWeight: 700,
          color: isActive ? GOLD_DIM : inkMuted,
        }}>{label}</span>
        {isActive && (
          <button
            onClick={() => onChange(defaultVal)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: GOLD_DIM, fontSize: 9, fontFamily: NU,
              padding: '2px 4px', letterSpacing: '0.5px',
            }}
          >Clear</button>
        )}
      </div>

      {/* Pill grid */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map(o => {
          const sel = o.value === value;
          return (
            <button
              key={o.value}
              onClick={() => onChange(o.value)}
              style={{
                padding: '7px 14px', borderRadius: 20,
                background: sel
                  ? (dark ? 'rgba(211,154,33,0.18)' : 'rgba(211,154,33,0.10)')
                  : 'transparent',
                border: `1px solid ${sel ? GOLD : (dark ? 'rgba(255,255,255,0.12)' : 'rgba(150,128,90,0.28)')}`,
                color: sel ? GOLD : (dark ? 'rgba(240,236,228,0.60)' : 'rgba(42,32,18,0.55)'),
                fontFamily: NU, fontSize: 12, fontWeight: sel ? 600 : 400,
                cursor: 'pointer', transition: 'all 0.15s',
                whiteSpace: 'nowrap',
              }}
            >{o.label}</button>
          );
        })}
      </div>
    </div>
  );
}

// ── FilterBar ────────────────────────────────────────────────────────────────
/**
 * Props:
 *  config          {filters:[]}        filterConfig object (from filterConfig.js)
 *  filters         object              active filter state { sort, venueType, ... }
 *  onFilterChange  (key, val) => void  per-key update handler
 *  filterOptions   object              dynamic options from extractFilterOptions
 *
 *  countryOptions  [{value,label}]     null = hide country dropdown
 *  countryValue    string              default 'all'
 *  onCountryChange (val) => void
 *
 *  regionOptions   [{value,label}]     null = hide region dropdown
 *  regionValue     string              default 'all'
 *  onRegionChange  (val) => void
 *
 *  count           number
 *  countLabel      string              e.g. "wedding venues"
 *  viewMode        "grid"|"list"
 *  onViewMode      (mode) => void
 *  mapOn           bool
 *  onToggleMap     () => void | null   null = hide map button
 *
 *  isMobile        bool
 *  dark            bool
 */
export default function FilterBar({
  config,
  filters = {},
  onFilterChange,
  filterOptions = {},
  countryOptions = null,
  countryValue = 'all',
  onCountryChange,
  regionOptions = null,
  regionValue = 'all',
  onRegionChange,
  regionLabel = 'Region',
  regionDefaultLabel = 'All Regions',
  count = 0,
  countLabel = 'listings',
  viewMode = 'grid',
  onViewMode,
  mapOn = false,
  onToggleMap = null,
  isMobile = false,
  dark = false,
}) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const dims    = config?.filters || [];
  const sortVal = filters.sort || 'recommended';

  const goldText  = dark ? 'rgba(211,154,33,0.85)' : 'rgba(140,110,50,0.90)';
  // muteText: darker on mobile for readability, lighter on desktop (more space, less need for contrast)
  const muteText  = isMobile
    ? (dark ? 'rgba(240,236,228,0.70)' : 'rgba(42,32,18,0.78)')
    : (dark ? 'rgba(255,255,255,0.30)' : 'rgba(50,40,25,0.40)');
  const borderC   = dark ? 'rgba(255,255,255,0.12)' : 'rgba(180,165,140,0.35)';
  // inkMuted: label/placeholder text, darker on mobile
  const inkMuted  = isMobile
    ? (dark ? 'rgba(240,236,228,0.72)' : 'rgba(42,32,18,0.75)')
    : (dark ? 'rgba(240,236,228,0.38)' : 'rgba(42,32,18,0.42)');
  const sheetBg   = dark ? '#0f0d0a' : STONE;
  const dividerC  = dark ? 'rgba(255,255,255,0.08)' : 'rgba(180,165,140,0.20)';

  // Count active (non-default) filters, used for the badge on the Refine pill
  const activeCount = [
    ...(countryOptions ? [{ val: countryValue, def: 'all' }] : []),
    ...(regionOptions?.length ? [{ val: regionValue, def: 'all' }] : []),
    ...dims.filter(d => !d.navigates).map(d => ({ val: filters[d.key] || 'All', def: 'All' })),
    { val: sortVal, def: 'recommended' },
  ].filter(f => f.val && f.val !== f.def).length;

  // Clear all active filters
  const clearAll = () => {
    if (countryOptions) onCountryChange?.('all');
    if (regionOptions?.length) onRegionChange?.('all');
    dims.filter(d => !d.navigates).forEach(d => onFilterChange?.(d.key, 'All'));
    onFilterChange?.('sort', 'recommended');
  };

  // ── MOBILE: compact pill bar + bottom sheet ──────────────────────────────
  if (isMobile) {
    return (
      <>
        {/* Compact bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px', height: 52,
        }}>
          {/* Result count */}
          <span style={{ fontFamily: NU, fontSize: 10, color: muteText }}>
            <span style={{ fontWeight: 700, color: goldText }}>{count}</span>
            {' '}{countLabel}
          </span>

          {/* Refine pill */}
          <button
            onClick={() => setSheetOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 16px', borderRadius: 20, flexShrink: 0,
              background: activeCount > 0
                ? (dark ? 'rgba(211,154,33,0.12)' : 'rgba(211,154,33,0.08)')
                : 'transparent',
              border: `1px solid ${activeCount > 0 ? GOLD : (dark ? 'rgba(255,255,255,0.15)' : 'rgba(150,128,90,0.35)')}`,
              color: activeCount > 0 ? GOLD : inkMuted,
              fontFamily: NU, fontSize: 10, fontWeight: 700,
              letterSpacing: '1px', textTransform: 'uppercase',
              cursor: 'pointer', transition: 'all 0.2s',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="6" x2="20" y2="6"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
              <line x1="11" y1="18" x2="13" y2="18"/>
            </svg>
            Refine
            {activeCount > 0 && (
              <span style={{
                background: GOLD, color: '#fff', borderRadius: '50%',
                width: 16, height: 16, fontSize: 9, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>{activeCount}</span>
            )}
          </button>
        </div>

        {/* Bottom sheet */}
        {sheetOpen && (
          <>
            <style>{`
              @keyframes lwd-sheet-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
              @keyframes lwd-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
            `}</style>

            {/* Backdrop, above MAP pill (z 900) and Aura (z ~850) */}
            <div
              onClick={() => setSheetOpen(false)}
              style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.52)', zIndex: 940,
                animation: 'lwd-backdrop-in 0.22s ease',
              }}
            />

            {/* Sheet */}
            <div style={{
              position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 950,
              background: sheetBg,
              borderRadius: '16px 16px 0 0',
              borderTop: `1px solid ${dark ? 'rgba(211,154,33,0.22)' : 'rgba(211,154,33,0.30)'}`,
              maxHeight: '85dvh', overflowY: 'auto',
              animation: 'lwd-sheet-up 0.30s cubic-bezier(0.32, 0.72, 0, 1)',
            }}>
              {/* Drag handle */}
              <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 0' }}>
                <div style={{
                  width: 36, height: 4, borderRadius: 2,
                  background: dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
                }} />
              </div>

              {/* Header */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 20px 8px',
              }}>
                <span style={{
                  fontFamily: NU, fontSize: 14, fontWeight: 700, letterSpacing: '0.3px',
                  color: dark ? '#f0ece4' : 'rgba(42,32,18,0.92)',
                }}>
                  Refine Results
                </span>
                <button
                  onClick={() => setSheetOpen(false)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: inkMuted, padding: 4, lineHeight: 1,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              {/* Thin gold rule under header */}
              <div style={{ height: 1, margin: '0 20px 20px', background: dark ? 'rgba(211,154,33,0.12)' : 'rgba(211,154,33,0.18)' }} />

              {/* Filter sections */}
              <div style={{ padding: '0 20px' }}>
                {/* Country */}
                {countryOptions && (
                  <SheetSection
                    label="Country"
                    value={countryValue}
                    defaultVal="all"
                    options={[{ value: 'all', label: 'All Countries' }, ...countryOptions]}
                    onChange={v => onCountryChange?.(v)}
                    dark={dark}
                  />
                )}

                {/* Region */}
                {regionOptions && regionOptions.length > 0 && (
                  <SheetSection
                    label={regionLabel}
                    value={regionValue}
                    defaultVal="all"
                    options={[{ value: 'all', label: regionDefaultLabel }, ...regionOptions]}
                    onChange={v => onRegionChange?.(v)}
                    dark={dark}
                  />
                )}

                {/* Config-driven dimensions */}
                {dims.filter(d => !d.navigates).map(dim => {
                  const toLabel = (o) => dim.optionLabels?.[o] || o;
                  const rawOpts = dim.staticOptions
                    ? dim.staticOptions.map(o => ({ value: o, label: toLabel(o) }))
                    : (filterOptions[dim.key] || []).map(o => ({ value: o, label: toLabel(o) }));
                  if (!rawOpts.length) return null;
                  return (
                    <SheetSection
                      key={dim.key}
                      label={dim.label}
                      value={filters[dim.key] || 'All'}
                      defaultVal="All"
                      options={[{ value: 'All', label: allLabel(dim.key) }, ...rawOpts]}
                      onChange={v => onFilterChange?.(dim.key, v)}
                      dark={dark}
                    />
                  );
                })}

                {/* Sort */}
                <SheetSection
                  label="Sort By"
                  value={sortVal}
                  defaultVal="recommended"
                  options={[
                    { value: 'recommended', label: 'Recommended' },
                    { value: 'rating',      label: 'Highest Rated' },
                    { value: 'price-low',   label: 'Price: Low to High' },
                    { value: 'price-high',  label: 'Price: High to Low' },
                  ]}
                  onChange={v => onFilterChange?.('sort', v)}
                  dark={dark}
                />
              </div>

              {/* Footer: clear + done */}
              {/* paddingBottom 88px: MAP pill is position:fixed bottom:28 (~44px tall)
                  so we need 28+44+16 = 88px of clearance to keep button above it */}
              <div style={{
                display: 'flex', gap: 10,
                padding: '16px 20px 88px',
                borderTop: `1px solid ${dividerC}`,
              }}>
                {activeCount > 0 && (
                  <button
                    onClick={() => { clearAll(); setSheetOpen(false); }}
                    style={{
                      flex: 1, padding: '13px', borderRadius: 8,
                      background: 'none',
                      border: `1px solid ${dark ? 'rgba(255,255,255,0.14)' : 'rgba(150,128,90,0.38)'}`,
                      color: inkMuted,
                      fontFamily: NU, fontSize: 11, fontWeight: 700,
                      letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer',
                    }}
                  >Clear All</button>
                )}
                <button
                  onClick={() => setSheetOpen(false)}
                  style={{
                    flex: 2, padding: '13px', borderRadius: 8,
                    background: dark ? GOLD : '#1a1a1a',
                    border: 'none', color: '#fff',
                    fontFamily: NU, fontSize: 11, fontWeight: 700,
                    letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer',
                  }}
                >
                  Show {count} {countLabel}
                </button>
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  // ── DESKTOP: inline filter row ───────────────────────────────────────────
  return (
    <div style={{
      maxWidth: 1280, margin: '0 auto',
      padding: '10px 48px',
      display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', gap: 16,
    }}>

      {/* Left: geo dropdowns + config dims + sort */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap' }}>

        {/* Country */}
        {countryOptions && (
          <Select
            label="Country"
            value={countryValue}
            defaultVal="all"
            onChange={v => onCountryChange?.(v)}
            options={[{ value: 'all', label: allLabel('country') }, ...countryOptions]}
            dark={dark}
          />
        )}

        {/* Region / Area */}
        {regionOptions && regionOptions.length > 0 && (
          <Select
            label={regionLabel}
            value={regionValue}
            defaultVal="all"
            onChange={v => onRegionChange?.(v)}
            options={[{ value: 'all', label: regionDefaultLabel }, ...regionOptions]}
            dark={dark}
          />
        )}

        {/* Config-driven dims, skip navigates:true */}
        {dims.filter(d => !d.navigates).map(dim => {
          const toLabel = (o) => dim.optionLabels?.[o] || o;
          const rawOpts = dim.staticOptions
            ? dim.staticOptions.map(o => ({ value: o, label: toLabel(o) }))
            : (filterOptions[dim.key] || []).map(o => ({ value: o, label: toLabel(o) }));

          if (!rawOpts.length) return null;

          return (
            <Select
              key={dim.key}
              label={dim.label}
              value={filters[dim.key] || 'All'}
              defaultVal="All"
              onChange={v => onFilterChange?.(dim.key, v)}
              options={[{ value: 'All', label: allLabel(dim.key) }, ...rawOpts]}
              dark={dark}
            />
          );
        })}

        {/* Sort, always last */}
        <Select
          label="Sort"
          value={sortVal}
          defaultVal="recommended"
          onChange={v => onFilterChange?.('sort', v)}
          options={[
            { value: 'recommended', label: 'Recommended' },
            { value: 'rating',      label: 'Highest Rated' },
            { value: 'price-low',   label: 'Price: Low → High' },
            { value: 'price-high',  label: 'Price: High → Low' },
          ]}
          dark={dark}
        />
      </div>

      {/* Right: count + view mode + map toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span style={{ fontFamily: NU, fontSize: 10, whiteSpace: 'nowrap', color: muteText }}>
          <span style={{ fontWeight: 600, color: goldText }}>{count}</span> {countLabel}
        </span>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          {/* Grid */}
          <button
            onClick={() => onViewMode?.('grid')}
            title="Grid view" aria-pressed={viewMode === 'grid'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 30, height: 30, padding: 0, cursor: 'pointer',
              background: viewMode === 'grid' ? (dark ? '#f0ece4' : '#1a1a1a') : 'transparent',
              color:      viewMode === 'grid' ? (dark ? '#0d0b09' : '#fff') : (dark ? 'rgba(255,255,255,0.40)' : 'rgba(50,40,25,0.40)'),
              border: `1px solid ${borderC}`, borderRadius: '3px 0 0 3px',
              transition: 'all 0.2s',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </button>

          {/* List */}
          <button
            onClick={() => onViewMode?.('list')}
            title="List view" aria-pressed={viewMode === 'list'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 30, height: 30, padding: 0, cursor: 'pointer',
              background: viewMode === 'list' ? (dark ? '#f0ece4' : '#1a1a1a') : 'transparent',
              color:      viewMode === 'list' ? (dark ? '#0d0b09' : '#fff') : (dark ? 'rgba(255,255,255,0.40)' : 'rgba(50,40,25,0.40)'),
              border: `1px solid ${borderC}`, borderLeft: 'none', borderRadius: 0,
              transition: 'all 0.2s',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="2" width="14" height="2.5" rx="1" stroke="currentColor" strokeWidth="1.2"/>
              <rect x="1" y="6.75" width="14" height="2.5" rx="1" stroke="currentColor" strokeWidth="1.2"/>
              <rect x="1" y="11.5" width="14" height="2.5" rx="1" stroke="currentColor" strokeWidth="1.2"/>
            </svg>
          </button>

          {/* Map */}
          {onToggleMap && (
            <button
              onClick={viewMode !== 'list' ? onToggleMap : undefined}
              title={mapOn ? 'Hide map' : 'Explore on map'}
              aria-pressed={mapOn}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                height: 30, padding: '0 10px',
                cursor: viewMode === 'list' ? 'default' : 'pointer',
                background: '#080808',
                color:  mapOn ? 'rgba(211,154,33,1)' : 'rgba(211,154,33,0.55)',
                border: `1px solid ${mapOn ? 'rgba(211,154,33,0.75)' : 'rgba(211,154,33,0.30)'}`,
                borderLeft: 'none', borderRadius: '0 3px 3px 0',
                fontFamily: NU, fontSize: 9, fontWeight: 600,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                whiteSpace: 'nowrap', opacity: viewMode === 'list' ? 0.6 : 1,
                transition: 'all 0.2s',
              }}
            >
              <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" transform="scale(0.7) translate(1,1)"/>
                <line x1="3" y1="6" x2="3" y2="13" transform="scale(0.7) translate(1,1)"/>
                <line x1="9" y1="3" x2="9" y2="10" transform="scale(0.7) translate(1,1)"/>
              </svg>
              Explore
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
